import React from 'react';
import { Switch, Route, Redirect, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { isClubRole } from '../Components/roles';
import MainLayout from './MainLayout';
import ClubHome from '../Pages/ClubHome';
import ManagePromotions from '../Pages/ManagePromotions';
import RedemptionPartner from '../Pages/RedemptionPartner';
import S7000Config from '../Pages/S7000Config';
import UserManagement from '../Pages/UserManagement';
import ManageLocations from '../Pages/ManageLocations';
import PromotionLocationMap from '../Pages/PromotionLocationMap';
import SinglePassCreation from '../Pages/SinglePassCreation';
import GrantSingleMember from '../Pages/GrantSingleMember';
import BulkPassCreation from '../Pages/BulkPassCreation';
import ReIssuePassCreation from '../Pages/ReIssuePassCreation';
import ReIssueSinglePass from '../Pages/ReIssueSinglePass';
import PromotionMemberMap from '../Pages/PromotionMemberMap';
import { JOB_PROCESS } from '../Components/useJobPoller';
import RedemptionReport from '../Pages/RedemptionReport';

// Which roles may open each club page (100=superadmin, 110=Club Admin,
// 101=Club Manager, 102=Club Receptionist).
const PAGE_ROLES = {
  home: [100, 110, 101, 102],
  promotions: [100, 110, 101],
  'redemption-partner': [100],
  s7000: [100],
  users: [100],
  locations: [100, 110],
  'promotion-location': [100, 110],
  'single-pass': [100, 110, 102],
  // Coupon issuing is split by promotion type: membership coupons are Member
  // offers (type 2), consumer coupon passes are Public promotions (type 1).
  // The old combined `grant-coupon` / `issue-coupon-members` showed both at once
  // and are retired; neither mapped onto one of these, so no alias is offered.
  'membership-coupon-single': [100, 110, 101],
  'membership-coupon-multiple': [100, 110, 101],
  'consumer-coupon-single': [100, 110, 101],
  'consumer-coupon-multiple': [100, 110, 101],
  // Bulk membership-pass upload stays superadmin-only; both are club-scoped (they
  // use the URL's issuerId).
  'bulk-passes': [100],
  // The four coupon pages above form two menu groups (membershipCouponMenuGroup
  // and consumerCouponMenuGroup in Drawer.js). Keep each group's pair in step:
  // a role granted one half and not the other gets a menu link that bounces
  // straight back to club home.
  // Re-issue is open to every club role, unlike the bulk uploads above.
  'reissue-passes': [100, 110, 101, 102],
  'reissue-single-pass': [100, 110, 101, 102],
  // Redemption reports are available to superadmin, club admin, and club manager.
  reports: [100, 110, 101],
  'redemption-reports': [100, 110, 101],
};

// The URL reflects the active club. Superadmin enters a club only by explicit
// selection (session-scoped selectedClub matching the URL). Club roles (110/101/
// 102) operate inside their single login-assigned club (user.issuerId) and can
// only reach the pages their role allows.
function ClubScope() {
  const { issuerId } = useParams();
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();
  const userRole = Number(user?.role ?? user?.userType ?? user?.type ?? user?.role_id ?? user?.roleId);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (!isAuthenticated) {
    return <Redirect to={{ pathname: '/login', state: { from: location } }} />;
  }

  // Entry rule.
  if (userRole === 100) {
    // Superadmin: unchanged — require an explicit selection matching the URL.
    const selectedClub = user?.selectedClub;
    if (!selectedClub || String(selectedClub.id) !== String(issuerId)) {
      return <Redirect to={{ pathname: '/ClubManagement' }} />;
    }
  } else if (isClubRole(userRole)) {
    // Club roles may only enter their own club.
    if (String(user?.issuerId) !== String(issuerId)) {
      return <Redirect to={{ pathname: '/' }} />;
    }
  } else {
    // Unknown roles have no club pages.
    return <Redirect to={{ pathname: '/' }} />;
  }

  const home = `/club/${issuerId}/home`;
  const canAccess = (page) => (PAGE_ROLES[page] || []).includes(userRole);
  const guard = (page, Component) => (props) =>
    canAccess(page) ? <Component {...props} /> : <Redirect to={home} />;

  return (
    <MainLayout>
      <Switch>
        <Route exact path="/club/:issuerId/home" render={guard('home', ClubHome)} />
        <Route exact path="/club/:issuerId/promotions" render={guard('promotions', ManagePromotions)} />
        <Route exact path="/club/:issuerId/redemption-partner" render={guard('redemption-partner', RedemptionPartner)} />
        <Route exact path="/club/:issuerId/s7000" render={guard('s7000', S7000Config)} />
        <Route exact path="/club/:issuerId/users" render={guard('users', UserManagement)} />
        <Route exact path="/club/:issuerId/locations" render={guard('locations', ManageLocations)} />
        <Route exact path="/club/:issuerId/promotion-location" render={guard('promotion-location', PromotionLocationMap)} />
        <Route exact path="/club/:issuerId/single-pass" render={guard('single-pass', SinglePassCreation)} />
        {/* One component per row, twice each — the pages differ only in the
            promotion type they offer and what they call themselves. */}
        <Route exact path="/club/:issuerId/membership-coupon-single" render={guard('membership-coupon-single', () => (
            <GrantSingleMember
                promotionType={2}

                heading="Issue Single Membership Coupon by Member Id or Mobile Number"
            />
        ))} />
        <Route exact path="/club/:issuerId/consumer-coupon-single" render={guard('consumer-coupon-single', () => (
            <GrantSingleMember
                promotionType={1}

                heading="Issue Single Consumer Coupon Pass by Member Id or Mobile Number"
            />
        ))} />
        <Route exact path="/club/:issuerId/bulk-passes" render={guard('bulk-passes', BulkPassCreation)} />
        <Route exact path="/club/:issuerId/reissue-passes" render={guard('reissue-passes', ReIssuePassCreation)} />
        <Route exact path="/club/:issuerId/reissue-single-pass" render={guard('reissue-single-pass', ReIssueSinglePass)} />
        {/* Distinct process codes so each page adopts only its own job: matching
            on one shared code, the consumer page would pick up a membership run
            after a refresh and show its counters as its own. */}
        <Route exact path="/club/:issuerId/membership-coupon-multiple" render={guard('membership-coupon-multiple', () => (
            <PromotionMemberMap
                promotionType={2}
                processCode={JOB_PROCESS.MEMBERSHIP_COUPON_BULK}

                heading="Issue Membership Coupons in Bulk"
            />
        ))} />
        <Route exact path="/club/:issuerId/consumer-coupon-multiple" render={guard('consumer-coupon-multiple', () => (
            <PromotionMemberMap
                promotionType={1}
                processCode={JOB_PROCESS.CONSUMER_COUPON_BULK}

                heading="Issue Consumer Coupon Passes in Bulk"
            />
        ))} />
        <Route exact path="/club/:issuerId/reports" render={guard('reports', RedemptionReport)} />
        <Route exact path="/club/:issuerId/redemption-reports" render={guard('redemption-reports', RedemptionReport)} />
        <Redirect to={home} />
      </Switch>
    </MainLayout>
  );
}

export default ClubScope;
